import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, TrendingDown, Save, RefreshCw, DollarSign } from 'lucide-react';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api' 
  : '/api';

function PriceMarginSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [margins, setMargins] = useState({
    STANDART: 0.0,
    VIP: 0.0,
    KOMISYONSUZ: 0.0
  });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchMargins();
  }, []);

  const fetchMargins = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-margins`, axiosConfig);
      setMargins(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch margins:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/price-margins`, margins, axiosConfig);
      alert('✅ Fiyat marjları başarıyla güncellendi!');
      setSaving(false);
    } catch (error) {
      console.error('Failed to save margins:', error);
      alert('❌ Fiyat marjı güncelleme hatası!');
      setSaving(false);
    }
  };

  const handleChange = (type, value) => {
    const numValue = parseFloat(value);
    if (numValue >= -100 && numValue <= 100) {
      setMargins(prev => ({
        ...prev,
        [type]: numValue
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Yükleniyor...</div>
      </div>
    );
  }

  // Örnek hesaplamalar
  const examplePrice = 188.30;
  const calculatePrice = (margin) => (examplePrice * (1 + margin / 100)).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Fiyat Marjı Ayarları</h1>
            <p className="text-gray-400">Hesap tiplerine göre alış/satış fiyat marjlarını ayarlayın</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            ← Geri Dön
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <div className="text-blue-400 text-2xl">💡</div>
            <div className="text-blue-300 text-sm">
              <strong>Fiyat Marjı Nedir?</strong>
              <p className="mt-2 text-gray-300">
                Gerçek piyasa fiyatına eklenen yüzdelik artış/azalış. Örneğin ASELS gerçek fiyatı ₺188.30 ise:
              </p>
              <ul className="mt-2 space-y-1 text-gray-400">
                <li>• STANDART +%5 → Kullanıcı ₺197.72 görür (daha pahalı)</li>
                <li>• VIP -%2 → Kullanıcı ₺184.53 görür (İNDİRİMLİ! 🔥)</li>
                <li>• KOMİSYONSUZ +%0 → Kullanıcı ₺188.30 görür (gerçek fiyat)</li>
              </ul>
              <div className="mt-3 text-yellow-400 text-xs">
                ⚠️ Negatif değerler indirim sağlar (VIP üyelere özel!)
              </div>
            </div>
          </div>
        </div>

        {/* Margin Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* STANDART */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-blue-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">STANDART</h3>
                <p className="text-gray-400 text-sm">Normal hesaplar</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Fiyat Marjı (%) - Sınırsız</label>
              <input
                type="number"
                step="1"
                value={margins.STANDART}
                onChange={(e) => handleChange('STANDART', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-center font-bold text-xl outline-none"
                placeholder="0"
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">ASELS Örnek (₺{examplePrice}):</div>
              <div className="text-white text-sm">+ %{margins.STANDART}</div>
              <div className="text-blue-400 font-bold text-xl">= ₺{calculatePrice(margins.STANDART)}</div>
              <div className="text-gray-500 text-xs mt-1">
                {margins.STANDART > 0 ? <TrendingUp className="w-3 h-3 inline text-red-400" /> : margins.STANDART < 0 ? <TrendingDown className="w-3 h-3 inline text-green-400" /> : ''} 
                {' '}₺{Math.abs(examplePrice * margins.STANDART / 100).toFixed(2)} fark
                {margins.STANDART < 0 && <span className="text-green-400 ml-1">(İndirimli!)</span>}
              </div>
            </div>
          </div>

          {/* VIP */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-yellow-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-500/10 p-3 rounded-lg">
                <TrendingDown className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">⭐ VIP</h3>
                <p className="text-gray-400 text-sm">Daha iyi fiyat</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Fiyat Marjı (%) - Sınırsız</label>
              <input
                type="number"
                step="1"
                value={margins.VIP}
                onChange={(e) => handleChange('VIP', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-center font-bold text-xl outline-none"
                placeholder="0"
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">ASELS Örnek (₺{examplePrice}):</div>
              <div className="text-white text-sm">+ %{margins.VIP}</div>
              <div className="text-yellow-400 font-bold text-xl">= ₺{calculatePrice(margins.VIP)}</div>
              <div className="text-gray-500 text-xs mt-1">
                {margins.VIP > 0 ? <TrendingUp className="w-3 h-3 inline text-red-400" /> : margins.VIP < 0 ? <TrendingDown className="w-3 h-3 inline text-green-400" /> : ''} 
                {' '}₺{Math.abs(examplePrice * margins.VIP / 100).toFixed(2)} fark
                {margins.VIP < 0 && <span className="text-green-400 ml-1">(İndirimli!)</span>}
              </div>
            </div>
          </div>

          {/* KOMISYONSUZ */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-green-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-500/10 p-3 rounded-lg">
                <RefreshCw className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">💎 KOMİSYONSUZ</h3>
                <p className="text-gray-400 text-sm">Gerçek fiyat</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Fiyat Marjı (%) - Sınırsız</label>
              <input
                type="number"
                step="1"
                value={margins.KOMISYONSUZ}
                onChange={(e) => handleChange('KOMISYONSUZ', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg text-center font-bold text-xl outline-none"
                placeholder="0"
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">ASELS Örnek (₺{examplePrice}):</div>
              <div className="text-white text-sm">+ %{margins.KOMISYONSUZ}</div>
              <div className="text-green-400 font-bold text-xl">= ₺{calculatePrice(margins.KOMISYONSUZ)}</div>
              <div className="text-gray-500 text-xs mt-1">Gerçek piyasa fiyatı</div>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700">
          <h2 className="text-white font-bold text-xl mb-4">Karşılaştırma Tablosu</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400">Varlık</th>
                  <th className="text-right py-3 px-4 text-gray-400">Gerçek Fiyat</th>
                  <th className="text-right py-3 px-4 text-blue-400">STANDART</th>
                  <th className="text-right py-3 px-4 text-yellow-400">VIP</th>
                  <th className="text-right py-3 px-4 text-green-400">KOMİSYONSUZ</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-700/50">
                  <td className="py-3 px-4 text-white">ASELS</td>
                  <td className="py-3 px-4 text-right text-gray-300">₺188.30</td>
                  <td className="py-3 px-4 text-right text-blue-400 font-bold">₺{(188.30 * (1 + margins.STANDART/100)).toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-yellow-400 font-bold">₺{(188.30 * (1 + margins.VIP/100)).toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-bold">₺{(188.30 * (1 + margins.KOMISYONSUZ/100)).toFixed(2)}</td>
                </tr>
                <tr className="border-b border-gray-700/50">
                  <td className="py-3 px-4 text-white">BTC</td>
                  <td className="py-3 px-4 text-right text-gray-300">$91,602</td>
                  <td className="py-3 px-4 text-right text-blue-400 font-bold">${(91602 * (1 + margins.STANDART/100)).toFixed(0)}</td>
                  <td className="py-3 px-4 text-right text-yellow-400 font-bold">${(91602 * (1 + margins.VIP/100)).toFixed(0)}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-bold">${(91602 * (1 + margins.KOMISYONSUZ/100)).toFixed(0)}</td>
                </tr>
                <tr className="border-b border-gray-700/50">
                  <td className="py-3 px-4 text-white">ETH</td>
                  <td className="py-3 px-4 text-right text-gray-300">$3,500</td>
                  <td className="py-3 px-4 text-right text-blue-400 font-bold">${(3500 * (1 + margins.STANDART/100)).toFixed(0)}</td>
                  <td className="py-3 px-4 text-right text-yellow-400 font-bold">${(3500 * (1 + margins.VIP/100)).toFixed(0)}</td>
                  <td className="py-3 px-4 text-right text-green-400 font-bold">${(3500 * (1 + margins.KOMISYONSUZ/100)).toFixed(0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={() => fetchMargins()}
            className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-4 rounded-lg transition-colors font-semibold flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Sıfırla
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 rounded-lg transition-all font-semibold flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PriceMarginSettings;
